import { Box, Button, Divider, Grid, Typography } from '@material-ui/core';
import { DialogSaurus } from '../dialog-saurus/dialog-saurus';
import { useCallback, useMemo, useState } from 'react';
import { useCadastros, useSessaoAtual } from 'services/app';
import { VotacoesModel } from 'model/api/gestao/votacao-desenvolvimento/votacoes-model';
import HTMLReactParser from 'html-react-parser';
import { formatarQuebrasLinha } from 'utils/formatar-quebras-linha';
import { useThemeQueries } from 'views/theme';
import { useStyles } from './dialog-tarefa-detalhes-styles';
import { RecolherIcon } from 'views/components/icons/recolher-icon';
import { LinkIcon } from 'views/components/icons/link-icon';
import { CircularLoading } from 'views/components/utils';
import classNames from 'classnames';
import { ConfirmarIcon } from 'views/components/icons/confirmar-icon';
import { SistemasRoadmapMock } from 'data/mocks/sistemas-roadmap-mock';
import { toDateString } from 'utils/to-date';
import { EnumTipoPessoas } from 'model/enums/enum-tipo-pessoas';
import { ListaRevendasVoto } from './components/lista-revendas-voto/lista-revendas-voto';

interface DialogTarefaDetalhesProps {
    openned: boolean;
    loading: boolean;
    model: VotacoesModel;
    handleClickVote: (id: string, tarefaId: string) => Promise<void>
    handleClickLink: (link: string) => void
    desabilitado: boolean
}

export const DialogTarefaDetalhes = ({
    openned,
    model,
    handleClickLink,
    handleClickVote,
    desabilitado,
}: DialogTarefaDetalhesProps) => {
    const [isCarregando, setIsCarregando] = useState<boolean>(false)
    const classes = useStyles()
    const { theme: { palette }, xs, isMobile } = useThemeQueries()
    const {
        fecharDialogTarefaDetalhes,
    } = useCadastros()
    const { tipoUsuario } = useSessaoAtual()
    const [modelo, setModelo] = useState<VotacoesModel>(model)
    const isSoftwareHouse = tipoUsuario() === EnumTipoPessoas.SoftwareHouse

    const carregando = useMemo(() => isCarregando, [isCarregando]);

    const handleVoteClick = useCallback(async () => {
        if (desabilitado) return
        setIsCarregando(true);
        await handleClickVote(modelo.id, modelo.clickup_id)
            .then(() => {
                setModelo({ ...modelo, votado: !modelo.votado, votos: modelo.votado ? modelo.votos - 1 : modelo.votos + 1 });
            })
            .catch(Boolean)
            .finally(() => {
                setIsCarregando(false);
            })
    }, [desabilitado, handleClickVote, modelo]);

    const handleLinkClick = useCallback(() => {
        handleClickLink(modelo.link);
    }, [handleClickLink, modelo.link]);

    // Formatar a descrição com quebras de linha convertidas para HTML
    const descricaoFormatada = useMemo(() => {
        return formatarQuebrasLinha(modelo.descricao);
    }, [modelo.descricao]);

    const formattedDate = useMemo(() => {
        return modelo?.data_cadastro
            ? toDateString(modelo.data_cadastro, 'DD/MM/YYYY HH:mm')
            : '';
    }, [modelo.data_cadastro]);

    const bottomArea = useMemo(() => {
        return (
            <Box px={2} pb={1}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12} sm alignItems='center' container>
                        <Typography variant="caption">
                            {modelo.clickup_id} - Criado em: {formattedDate}
                        </Typography>
                    </Grid>
                    <Grid item sm="auto" xs={6}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth={xs}
                            size="large"
                            disabled={isCarregando}
                            onClick={handleLinkClick}
                        >
                            <LinkIcon tipo="BUTTON_PRIMARY" />
                            Visualizar
                        </Button>
                    </Grid>
                    <Grid item sm="auto" xs={6}>
                        <Button
                            variant={modelo.votado ? 'contained' : "outlined"}
                            className={classNames(modelo.votado ? classes.btnVotado : null, desabilitado && classes.disabled)}
                            color="primary"
                            fullWidth={xs}
                            size="large"
                            onClick={handleVoteClick}
                            disabled={isCarregando}
                        >
                            {modelo.votado ? <ConfirmarIcon tipo="BUTTON" /> : <RecolherIcon tipo="BUTTON" />}
                            Votos: {modelo.votos}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        )
    }, [modelo.clickup_id, modelo.votado, modelo.votos, formattedDate, xs, isCarregando, handleLinkClick, classes.btnVotado, classes.disabled, desabilitado, handleVoteClick])

    return (
        <DialogSaurus
            aberto={openned}
            titulo={"Detalhes da Tarefa"}
            tamanho={isSoftwareHouse ? 'md' : "sm"}
            isDivider
            titleClasses={classes.titleDialog}
            isButtonTitleClose
            handleClickClose={fecharDialogTarefaDetalhes}
            bottomArea={bottomArea}
        >
            {carregando && <CircularLoading tipo="FULLSIZED" />}
            <Box width="100%" p={1} maxHeight={400}>
                <Grid container spacing={2} style={{ flexDirection: isMobile ? 'column-reverse' : 'row' }}>
                    {isSoftwareHouse && (
                        <ListaRevendasVoto votacaoId={model.id} />
                    )}
                    <Grid item xs={12} md={isSoftwareHouse ? 8 : 12} className={classes.content}>
                        <Grid>
                            <Typography variant='h6' className={classes.weight}>
                                {modelo.titulo}
                            </Typography>
                        </Grid>
                        <Box display={"flex"} gridGap={4}>
                            {modelo?.sistema && modelo.sistema.split(';').map((sistema, index) => (
                                <Box display="flex" key={`sistema-${index}`}>
                                    <Grid className={classes.tipoBox} style={{
                                        backgroundColor: palette.primary.main,
                                    }}>
                                        <Typography
                                            color="textPrimary"
                                            variant="caption"
                                            style={{
                                                color: palette.common.white,
                                                fontWeight: 600,
                                                whiteSpace: "nowrap"
                                            }}
                                        >
                                            {SistemasRoadmapMock.find(s => s.Key === sistema)?.Value ?? sistema}
                                        </Typography>
                                    </Grid>
                                </Box>
                            ))}
                        </Box>
                        <Box>
                            <Box mb={2}>
                                <Typography variant='h6'>
                                    <Box fontWeight={500} mb={2}>
                                        Descrição
                                    </Box>
                                </Typography>
                                {HTMLReactParser(descricaoFormatada)}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </DialogSaurus>
    );
};